// Profile Modal - Opens from sidebar profile button
// Custom modal form implementation for user profile management

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEntity, useUpdateEntity } from '../../hooks/useSWR';
import useSWRMutation from 'swr/mutation';
import { API_BASE_URL } from '../../config/apiConfig';
import { XIcon, UserIcon, KeyIcon, LockIcon, ExclamationCircleIcon, SpinnerIcon, InfoIcon } from '../ui/Icons';
import AppearanceSettingsPanel from '../profile/AppearanceSettingsPanel';

// Password change mutation fetcher (custom endpoint)
const passwordChangeFetcher = async (url, { arg }) => {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    },
    body: JSON.stringify(arg)
  });

  if (!response.ok) {
    const error = new Error('Password change failed');
    error.info = await response.json().catch(() => ({}));
    error.message = error.info?.message || 'Failed to change password';
    throw error;
  }

  return response.json();
};

const ProfileModal = ({ isOpen, onClose, initialTab = 'profile' }) => {
  const { theme, isDark } = useTheme();
  const { user, updateUser, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [notification, setNotification] = useState(null);
  const [errors, setErrors] = useState({});

  // Extract user ID from JWT token (with fallback to localStorage)
  const getCurrentUserId = () => {
    // First try from context
    if (user?.id) {
      return user.id;
    }
    
    // Fallback: parse user from localStorage if context is still loading
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        const parsedUser = JSON.parse(savedUser);
        return parsedUser?.id;
      }
    } catch (error) {
      console.error('Error parsing user from localStorage:', error);
    }
    
    return null;
  };

  const currentUserId = getCurrentUserId();

  // SWR hooks for data fetching and mutations
  const { data: currentUserData, isLoading: isUserLoading, isError: userError, mutate: mutateUser } = useEntity(
    '/users', 
    currentUserId
  );
  const updateProfile = useUpdateEntity('/users');
  const { trigger: changePassword, isMutating: isPasswordLoading } = useSWRMutation(
    '/profile/change-password', 
    passwordChangeFetcher
  );

  // Profile form data
  const [profileData, setProfileData] = useState({
    userFirstName: '',
    userLastName: '',
    userDisplayName: '',
    userEmail: ''
  });

  // Password form data
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Load user data when SWR fetches it successfully
  useEffect(() => {
    if (currentUserData) {
      setProfileData({
        userFirstName: currentUserData.userFirstName || '',
        userLastName: currentUserData.userLastName || '',
        userDisplayName: currentUserData.userDisplayName || '',
        userEmail: currentUserData.userEmail || ''
      });
    }
  }, [currentUserData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('profile');
      setNotification(null);
      setErrors({});
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [isOpen]);

  // Validation functions
  const validateProfile = () => {
    const newErrors = {};
    
    if (!profileData.userFirstName?.trim()) {
      newErrors.userFirstName = 'First name is required';
    }
    
    if (!profileData.userLastName?.trim()) {
      newErrors.userLastName = 'Last name is required';
    }
    
    if (!profileData.userEmail?.trim()) {
      newErrors.userEmail = 'Email is required';
    } else {
      // Backend validation: email must not exceed 255 characters
      if (profileData.userEmail.length > 255) {
        newErrors.userEmail = 'Email must not exceed 255 characters';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(profileData.userEmail)) {
          newErrors.userEmail = 'Please enter a valid email address';
        }
      }
    }
    
    return newErrors;
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData.oldPassword?.trim()) {
      newErrors.oldPassword = 'Current password is required';
    }
    
    if (!passwordData.newPassword?.trim()) {
      newErrors.newPassword = 'New password is required';
    } else {
      // Backend requires minimum 8 characters and maximum 128 characters
      if (passwordData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters long';
      } else if (passwordData.newPassword.length > 128) {
        newErrors.newPassword = 'Password must not exceed 128 characters';
      } else {
        // Additional strength validation (optional but recommended)
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (!passwordRegex.test(passwordData.newPassword)) {
          newErrors.newPassword = 'Password should contain uppercase, lowercase, number, and special character';
        }
      }
    }
    
    if (!passwordData.confirmPassword?.trim()) {
      newErrors.confirmPassword = 'Password confirmation is required';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'New passwords do not match';
    }
    
    return newErrors;
  };

  // Handle profile update using SWR
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateProfile();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    
    try {
      // Prepare data according to UpdateUserDto
      const updateData = {
        userFirstName: profileData.userFirstName,
        userLastName: profileData.userLastName,
        userDisplayName: profileData.userDisplayName || null,
        userEmail: profileData.userEmail
      };

      // Use SWR-based update function with the current user ID
      const updatedUserData = await updateProfile(currentUserId, updateData);
      
      // Update user context with the response data
      const updatedUser = { ...user, ...updatedUserData };
      updateUser(updatedUser);

      // Invalidate the SWR cache to refetch fresh data
      await mutateUser();

      setNotification({
        type: 'success',
        message: 'Profile updated successfully!'
      });

      // Close the modal after successful update
      setTimeout(() => {
        onClose();
      }, 1500); // Give user time to see the success message
      
    } catch (error) {
      setNotification({
        type: 'error',
        message: error.message || 'Failed to update profile'
      });
    }
  };

  // Handle password change using SWR
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    const validationErrors = validatePassword();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    
    try {
      // Use SWR mutation for password change
      const result = await changePassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });

      setNotification({
        type: 'success',
        message: result.message || 'Password changed successfully! You will be logged out shortly.'
      });
      
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Clear errors
      setErrors({});
      
      // Force logout after 3 seconds (backend invalidates all sessions)
      setTimeout(() => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }, 3000);
      
    } catch (error) {
      setNotification({
        type: 'error',
        message: error.message || 'Failed to change password'
      });
    }
  };

  // Clear notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className={`absolute inset-0 transition-opacity ${
          isDark
            ? 'bg-black/80 backdrop-blur-md'
            : 'bg-black/50 backdrop-blur-sm'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`relative ${theme.colors.background} rounded-lg ${theme.shadow.xl} max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-modal-title"
      >
        {/* Modal Header */}
        <div className={`flex items-center justify-between p-6 border-b ${theme.border.light}`}>
          <div>
            <h2
              id="profile-modal-title"
              className={`text-2xl font-bold ${theme.text.primary}`}
            >
              User Profile
            </h2>
            <p className={`${theme.text.muted} mt-1`}>
              Manage your personal information and settings
            </p>
          </div>
          <button
            onClick={onClose}
            className={`${theme.text.muted} hover:${theme.text.primary} transition-colors`}
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${theme.border.light}`}>
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'profile'
                ? `${theme.text.primary} border-b-2 ${theme.colors.primary.replace('bg-', 'border-')}`
                : `${theme.text.muted} hover:${theme.text.primary}`
            }`}
          >
            <UserIcon className="w-4 h-4 inline mr-2" />
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('appearance')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'appearance'
                ? `${theme.text.primary} border-b-2 ${theme.colors.primary.replace('bg-', 'border-')}`
                : `${theme.text.muted} hover:${theme.text.primary}`
            }`}
          >
            Εμφάνιση
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'password'
                ? `${theme.text.primary} border-b-2 ${theme.colors.primary.replace('bg-', 'border-')}`
                : `${theme.text.muted} hover:${theme.text.primary}`
            }`}
          >
            <KeyIcon className="w-4 h-4 inline mr-2" />
            Change Password
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Notification */}
          {notification && (
            <div className={`mb-6 p-4 rounded-lg ${
              notification.type === 'success' 
                ? `${theme.colors.success} ${theme.text.inverse}` 
                : `${theme.colors.danger} ${theme.text.inverse}`
            }`}>
              {notification.message}
            </div>
          )}

          {/* Loading State */}
          {(authLoading || (isUserLoading && !currentUserData) || !currentUserId) && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <SpinnerIcon className="w-6 h-6 text-blue-500" />
                <span className={`${theme.text.primary}`}>
                  {authLoading ? 'Authenticating...' : !currentUserId ? 'Loading authentication...' : 'Loading user profile...'}
                </span>
              </div>
            </div>
          )}

          {/* Not Authenticated State */}
          {!authLoading && !currentUserId && !localStorage.getItem('authToken') && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-yellow-500 mb-2">
                  <LockIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className={`${theme.text.primary} mb-2`}>Not Authenticated</p>
                <p className={`text-sm ${theme.text.muted} mb-4`}>Please log in to view your profile</p>
                <button
                  onClick={() => window.location.href = '/login'}
                  className={`px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded-lg hover:opacity-90 transition-opacity`}
                >
                  Go to Login
                </button>
              </div>
            </div>
          )}

          {/* Error State */}
          {userError && currentUserId && (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="text-red-500 mb-2">
                  <ExclamationCircleIcon className="w-12 h-12 mx-auto" />
                </div>
                <p className={`${theme.text.primary} mb-2`}>Failed to load user profile</p>
                <p className={`text-sm ${theme.text.muted} mb-4`}>{userError.message}</p>
                <button
                  onClick={() => mutateUser()}
                  className={`px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded-lg hover:opacity-90 transition-opacity`}
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && !authLoading && currentUserId && !isUserLoading && !userError && (currentUserData || user) && (
            <form onSubmit={handleProfileUpdate} className="space-y-6">
              {/* User Avatar */}
              <div className="flex items-center mb-6">
                <div className={`h-20 w-20 ${theme.colors.primary} ${theme.text.inverse} rounded-full flex items-center justify-center font-bold text-2xl ${theme.shadow.lg} mr-4`}>
                  {currentUserData?.userFirstName?.[0] || user?.userFirstName?.[0] || 'U'}
                  {currentUserData?.userLastName?.[0] || user?.userLastName?.[0] || 'U'}
                </div>
                <div>
                  <h3 className={`text-xl font-semibold ${theme.text.primary}`}>
                    {currentUserData?.userDisplayName || 
                     user?.userDisplayName || 
                     `${currentUserData?.userFirstName || user?.userFirstName || ''} ${currentUserData?.userLastName || user?.userLastName || ''}`.trim() || 
                     'User'}
                  </h3>
                  <p className={`${theme.text.muted}`}>
                    {currentUserData?.userEmail || user?.userEmail || 'No email'}
                  </p>
                  <p className={`text-sm ${theme.text.muted} capitalize`}>
                    Role: {currentUserData?.role?.name || user?.role?.name || 'User'}
                  </p>
                  {isUserLoading && (
                    <p className={`text-sm ${theme.text.muted}`}>Loading user data...</p>
                  )}
                  {userError && (
                    <p className="text-sm text-red-500">Error loading user data</p>
                  )}
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={profileData.userFirstName}
                    onChange={(e) => setProfileData({ ...profileData, userFirstName: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.userFirstName ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter first name"
                  />
                  {errors.userFirstName && (
                    <p className="text-red-500 text-sm mt-1">{errors.userFirstName}</p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={profileData.userLastName}
                    onChange={(e) => setProfileData({ ...profileData, userLastName: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.userLastName ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter last name"
                  />
                  {errors.userLastName && (
                    <p className="text-red-500 text-sm mt-1">{errors.userLastName}</p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={profileData.userDisplayName}
                    onChange={(e) => setProfileData({ ...profileData, userDisplayName: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter display name (optional)"
                  />
                  <p className={`text-sm ${theme.text.muted} mt-1`}>Leave empty to auto-generate from first and last name</p>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={profileData.userEmail}
                    onChange={(e) => setProfileData({ ...profileData, userEmail: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.userEmail ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter email address"
                  />
                  {errors.userEmail && (
                    <p className="text-red-500 text-sm mt-1">{errors.userEmail}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 border ${theme.border.light} ${theme.text.primary} rounded-lg hover:${theme.colors.backgroundSecondary} transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUserLoading}
                  className={`px-4 py-2 ${theme.colors.primary} ${theme.text.inverse} rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2`}
                >
                  {isUserLoading ? 'Loading...' : 'Update Profile'}
                </button>
              </div>
            </form>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && !authLoading && currentUserId && (
            <AppearanceSettingsPanel />
          )}

          {/* Password Tab */}
          {activeTab === 'password' && !authLoading && currentUserId && !isUserLoading && !userError && (currentUserData || user) && (
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <div className={`p-4 rounded-lg ${theme.colors.backgroundSecondary} border ${theme.border.light}`}>
                <div className="flex items-center gap-3 mb-3">
                  <InfoIcon className="w-5 h-5 text-blue-500" />
                  <h3 className={`text-lg font-medium ${theme.text.primary}`}>Password Requirements</h3>
                </div>
                <ul className={`text-sm ${theme.text.muted} space-y-1`}>
                  <li>• At least 8 characters long (maximum 128)</li>
                  <li>• Contains uppercase and lowercase letters (recommended)</li>
                  <li>• Contains at least one number (recommended)</li>
                  <li>• Contains at least one special character (@$!%*?&) (recommended)</li>
                </ul>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                  Current Password *
                </label>
                <input
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.oldPassword ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter current password"
                />
                {errors.oldPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.oldPassword}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                  New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.newPassword ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter new password"
                />
                {errors.newPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text.primary} mb-2`}>
                  Confirm New Password *
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg ${theme.colors.background} ${theme.text.primary} ${theme.border.light} focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : ''
                  }`}
                  placeholder="Confirm new password"
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 border ${theme.border.light} ${theme.text.primary} rounded-lg hover:${theme.colors.backgroundSecondary} transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPasswordLoading}
                  className={`px-4 py-2 ${theme.colors.danger} ${theme.text.inverse} rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2`}
                >
                  {isPasswordLoading && (
                    <SpinnerIcon className="w-4 h-4" />
                  )}
                  Change Password
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
